import React, { useState } from 'react';

const EDITABLE_FIELDS = [
  { key: 'name', label: 'Name', placeholder: 'e.g. 1g Gold 999,9', wide: true },
  { key: 'weight', label: 'Weight', placeholder: '1g' },
  { key: 'buyPrice', label: 'Nakup', placeholder: '€139,00' },
  { key: 'sellPrice', label: 'Odkup', placeholder: '€132,00' },
];

function itemKey(item) {
  return String(item?.name || '').trim().toLowerCase();
}

function isHidden(hidden, item) {
  if (!Array.isArray(hidden)) return false;
  const k = itemKey(item);
  return hidden.some((h) => String(h).trim().toLowerCase() === k);
}

/**
 * Per-section item editor. Lets the admin hide individual scraped rows from
 * the broadcast output and add custom extra rows that get appended at the end.
 *
 * Props:
 *   sectionKey:   string
 *   sectionLabel: string
 *   fetchedItems: Item[]  (latest scraped rows for this section)
 *   config:       { hidden: string[], extras: Item[] }
 *   onChange:     (next) => void
 */
export default function ItemEditor({ sectionKey, sectionLabel, fetchedItems, config, onChange }) {
  const hidden = config?.hidden || [];
  const extras = config?.extras || [];

  const [draft, setDraft] = useState({ name: '', weight: '', buyPrice: '', sellPrice: '' });

  const toggleHidden = (item) => {
    const key = itemKey(item);
    const next = isHidden(hidden, item)
      ? hidden.filter((h) => String(h).trim().toLowerCase() !== key)
      : [...hidden, item.name];
    onChange({ hidden: next, extras });
  };

  const removeExtra = (idx) => {
    onChange({ hidden, extras: extras.filter((_, i) => i !== idx) });
  };

  const updateExtra = (idx, field, value) => {
    onChange({
      hidden,
      extras: extras.map((e, i) => (i === idx ? { ...e, [field]: value } : e)),
    });
  };

  const addExtra = () => {
    if (!draft.name.trim()) return;
    onChange({ hidden, extras: [...extras, { ...draft }] });
    setDraft({ name: '', weight: '', buyPrice: '', sellPrice: '' });
  };

  const resetHidden = () => {
    onChange({ hidden: [], extras });
  };

  const visibleCount = (fetchedItems || []).filter((it) => !isHidden(hidden, it)).length;

  return (
    <div className="item-editor">
      <div className="item-editor-header">
        <h3>Rows — {sectionLabel}</h3>
        <span className="item-editor-stats">
          {visibleCount}/{(fetchedItems || []).length} scraped
          {extras.length > 0 && ` · +${extras.length} custom`}
        </span>
      </div>

      {(!fetchedItems || fetchedItems.length === 0) && (
        <p className="item-editor-empty">
          No scraped data yet. Run a scrape or trigger a broadcast to populate rows.
        </p>
      )}

      {fetchedItems && fetchedItems.length > 0 && (
        <>
          <div className="item-editor-toolbar">
            <span className="item-editor-hint">Toggle rows to include or exclude them.</span>
            {hidden.length > 0 && (
              <button type="button" className="btn-link" onClick={resetHidden}>
                Show all ({hidden.length} hidden)
              </button>
            )}
          </div>
          <ul className="fetched-items">
            {fetchedItems.map((item, idx) => {
              const hiddenNow = isHidden(hidden, item);
              return (
                <li
                  key={`${item.name}-${idx}`}
                  className={`fetched-item ${hiddenNow ? 'hidden' : ''}`}
                >
                  <label className="toggle small">
                    <input
                      type="checkbox"
                      checked={!hiddenNow}
                      onChange={() => toggleHidden(item)}
                    />
                    <span className="slider" />
                  </label>
                  <span className="fetched-name" title={item.name}>{item.name}</span>
                  <span className="fetched-cell">{item.weight || ''}</span>
                  <span className="fetched-cell">{item.buyPrice || '-'}</span>
                  <span className="fetched-cell">{item.sellPrice || '-'}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className="item-editor-section">
        <h4>Custom rows</h4>
        <p className="item-editor-hint">
          Manually-added rows appear after the fetched list in the output.
        </p>

        {extras.length > 0 && (
          <ul className="extra-items">
            {extras.map((item, idx) => (
              <li key={idx} className="extra-item">
                {EDITABLE_FIELDS.map((f) => (
                  <input
                    key={f.key}
                    type="text"
                    className={f.wide ? 'extra-input wide' : 'extra-input'}
                    placeholder={f.label}
                    value={item[f.key] || ''}
                    onChange={(e) => updateExtra(idx, f.key, e.target.value)}
                  />
                ))}
                <button
                  type="button"
                  className="extra-remove"
                  onClick={() => removeExtra(idx)}
                  title="Remove row"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="extra-item new-row">
          {EDITABLE_FIELDS.map((f) => (
            <input
              key={f.key}
              type="text"
              className={f.wide ? 'extra-input wide' : 'extra-input'}
              placeholder={f.placeholder || f.label}
              value={draft[f.key]}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addExtra();
                }
              }}
            />
          ))}
          <button
            type="button"
            className="btn-add-row"
            onClick={addExtra}
            disabled={!draft.name.trim()}
            title="Add custom row"
          >
            +
          </button>
        </div>
      </div>

      <p className="item-editor-footnote">
        Section also respects the column list above — hidden rows and custom rows both
        only show their configured columns.
      </p>
    </div>
  );
}

export { itemKey };
